import { useState, useCallback, useRef } from 'react'
import { backfillService } from '../services/backfillService'
import type {
  BackfillPreviewClientItem,
  BackfillGrantResponse,
  BackfillExecutionLog,
  BackfillSummary,
} from '../types'
import axios from 'axios'

export function useBackfillGrant() {
  const [isRunning, setIsRunning] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [progress, setProgress] = useState(0)
  const [total, setTotal] = useState(0)
  const [logs, setLogs] = useState<BackfillExecutionLog[]>([])
  const [summary, setSummary] = useState<BackfillSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const pauseRef = useRef(false)
  const cancelRef = useRef(false)

  const execute = useCallback(
    async (
      clients: BackfillPreviewClientItem[],
      ruleIds: number[],
      backfillReason: string,
    ) => {
      setIsRunning(true)
      setIsPaused(false)
      setError(null)
      setLogs([])
      setSummary(null)
      pauseRef.current = false
      cancelRef.current = false

      const eligibleClients = clients.filter(
        (c) => c.eligible_rule_ids.length > 0,
      )
      setTotal(eligibleClients.length)
      setProgress(0)

      const allLogs: BackfillExecutionLog[] = []
      let totalGranted = 0
      let totalSkipped = 0
      let totalErrors = 0

      for (let i = 0; i < eligibleClients.length; i++) {
        // Check for cancel
        if (cancelRef.current) {
          break
        }

        // Check for pause
        while (pauseRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 200))
          if (cancelRef.current) break
        }
        if (cancelRef.current) break

        const client = eligibleClients[i]
        const clientRuleIds = client.eligible_rule_ids.filter((id) =>
          ruleIds.includes(id),
        )

        if (clientRuleIds.length === 0) {
          setProgress(i + 1)
          continue
        }

        try {
          const response: BackfillGrantResponse = await backfillService.grant({
            client_id: client.id,
            rule_ids: clientRuleIds,
            backfill_reason: backfillReason,
          })

          const log: BackfillExecutionLog = {
            clientId: client.id,
            clientName: client.name,
            clientRut: client.rut,
            results: response.results,
            timestamp: new Date().toISOString(),
          }

          for (const r of response.results) {
            if (r.status === 'granted') totalGranted++
            else if (r.status === 'skipped') totalSkipped++
            else totalErrors++
          }

          allLogs.push(log)
          setLogs((prev) => [...prev, log])
        } catch (err) {
          let errorMsg = 'Error desconocido'
          if (axios.isAxiosError(err)) {
            errorMsg = err.response?.data?.detail || err.message
          }

          const log: BackfillExecutionLog = {
            clientId: client.id,
            clientName: client.name,
            clientRut: client.rut,
            results: clientRuleIds.map((ruleId) => ({
              rule_id: ruleId,
              rule_name: '',
              status: 'error' as const,
              reason: errorMsg,
            })),
            timestamp: new Date().toISOString(),
          }

          totalErrors += clientRuleIds.length
          allLogs.push(log)
          setLogs((prev) => [...prev, log])
        }

        setProgress(i + 1)
      }

      const finalSummary: BackfillSummary = {
        totalProcessed: eligibleClients.length,
        totalGranted,
        totalSkipped,
        totalErrors,
        logs: allLogs,
      }
      setSummary(finalSummary)
      setIsRunning(false)
    },
    [],
  )

  const pause = useCallback(() => {
    pauseRef.current = true
    setIsPaused(true)
  }, [])

  const resume = useCallback(() => {
    pauseRef.current = false
    setIsPaused(false)
  }, [])

  const cancel = useCallback(() => {
    cancelRef.current = true
    pauseRef.current = false
    setIsPaused(false)
  }, [])

  const reset = useCallback(() => {
    setIsRunning(false)
    setIsPaused(false)
    setProgress(0)
    setTotal(0)
    setLogs([])
    setSummary(null)
    setError(null)
    pauseRef.current = false
    cancelRef.current = false
  }, [])

  return {
    isRunning,
    isPaused,
    progress,
    total,
    logs,
    summary,
    error,
    execute,
    pause,
    resume,
    cancel,
    reset,
  }
}
